import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { Layout, LanguageButton } from '@components';
import { useLanguage } from '@hooks';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const StyledResumeContainer = styled.div`
  margin: 40px auto;
  max-width: 1200px;
  padding: 0 40px;
  position: relative;
  min-height: auto;
  height: auto;

  &::before {
    content: '';
    position: absolute;
    top: -80px;
    left: 50%;
    transform: translateX(-50%);
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(100, 255, 218, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    z-index: -1;
  }

  @media (max-width: 768px) {
    margin: 20px auto;
    padding: 0 20px;

    .markdown-content h1 {
      font-size: clamp(28px, 8vw, 45px);
      white-space: normal;
      word-break: keep-all;
      overflow-wrap: normal;
    }
  }

  @media (max-width: 480px) {
    padding: 0 15px;

    .markdown-content h1 {
      font-size: clamp(24px, 10vw, 36px);
      margin: 0 0 20px 0;
    }
  }

  .markdown-content {
    width: 100%;
    overflow: visible;

    h1 {
      color: var(--lightest-slate);
      font-size: clamp(32px, 6vw, 60px);
      margin: 0 0 30px 0;
      font-weight: 600;
      line-height: 1.3;
      text-align: center;
      background: linear-gradient(135deg, var(--lightest-slate), var(--green));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      position: relative;
      white-space: normal;
      overflow: visible;
      width: 100%;
      max-width: 100%;
      word-wrap: break-word;
      hyphens: auto;

      &::after {
        content: '';
        position: absolute;
        bottom: -15px;
        left: 50%;
        transform: translateX(-50%);
        width: 100px;
        height: 3px;
        background: linear-gradient(90deg, transparent, var(--green), transparent);
        border-radius: 2px;
      }
    }

    h2 {
      color: var(--green);
      font-size: clamp(24px, 5vw, 32px);
      margin: 50px 0 25px 0;
      font-weight: 600;
      position: relative;
      padding-left: 30px;

      &:before {
        content: '▶';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: 0.8em;
        animation: pulse 2s ease-in-out infinite;
      }

      &:after {
        content: '';
        position: absolute;
        bottom: -8px;
        left: 30px;
        width: 80px;
        height: 2px;
        background: linear-gradient(90deg, var(--green), transparent);
        border-radius: 1px;
      }
    }

    h3 {
      color: var(--lightest-slate);
      font-size: clamp(18px, 4vw, 24px);
      margin: 30px 0 15px 0;
      font-weight: 600;
      position: relative;
      padding-left: 20px;

      &:before {
        content: '●';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: 0.6em;
        top: 50%;
        transform: translateY(-50%);
      }
    }

    h4 {
      color: var(--slate);
      font-size: clamp(16px, 3vw, 18px);
      margin: 20px 0 10px 0;
      font-weight: 500;
      font-style: italic;
      opacity: 0.9;
    }

    p {
      color: var(--slate);
      font-size: var(--fz-lg);
      line-height: 1.8;
      margin: 0 0 20px 0;
      text-align: justify;
      word-wrap: break-word;
      overflow-wrap: break-word;
      hyphens: auto;
    }

    ul {
      padding-left: 0;
      margin: 20px 0;
      list-style: none;
      width: 100%;
      overflow: visible;

      li {
        color: var(--slate);
        font-size: var(--fz-md);
        line-height: 1.8;
        margin: 12px 0;
        position: relative;
        padding-left: 30px;
        transition: all 0.3s ease;
        word-wrap: break-word;
        overflow-wrap: break-word;
        hyphens: auto;
        width: 100%;

        &:before {
          content: '▸';
          position: absolute;
          left: 0;
          color: var(--green);
          font-size: var(--fz-sm);
          line-height: 1.8;
          transition: all 0.3s ease;
        }

        &:hover {
          color: var(--lightest-slate);
          transform: translateX(5px);

          &:before {
            color: #00d4ff;
            transform: scale(1.2);
          }
        }
      }
    }

    strong {
      color: var(--lightest-slate);
      font-weight: 600;
    }

    em {
      color: var(--green);
      font-style: normal;
      font-weight: 500;
    }

    a {
      color: var(--green);
      text-decoration: none;
      position: relative;
      transition: all 0.3s ease;

      &:hover {
        color: #00d4ff;
      }

      &:after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        width: 0;
        height: 1px;
        background: var(--green);
        transition: width 0.3s ease;
      }

      &:hover:after {
        width: 100%;
      }
    }
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const StyledResumeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px 0;
  border-bottom: 1px solid rgba(100, 255, 218, 0.1);
  min-height: 60px;
  width: 100%;
  overflow: visible;

  .header-left {
    display: flex;
    align-items: center;
    gap: 20px;
    flex: 1;
    min-width: 0;

    h1 {
      color: var(--lightest-slate);
      font-size: clamp(20px, 3.5vw, 32px);
      margin: 0;
      font-weight: 600;
      background: linear-gradient(135deg, var(--lightest-slate), var(--green));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 15px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    align-items: stretch;
    min-height: auto;

    .header-left {
      justify-content: center;

      h1 {
        font-size: clamp(18px, 5vw, 28px);
        white-space: normal;
        text-align: center;
      }
    }

    .header-right {
      justify-content: center;
    }
  }

  @media (max-width: 480px) {
    .header-left h1 {
      font-size: clamp(16px, 6vw, 24px);
    }
  }
`;

const StyledDownloadButton = styled.div`
  position: relative;
  display: inline-block;

  .download-button {
    ${({ theme }) => theme.mixins.smallButton};
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px -10px var(--green-tint);
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .download-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    background-color: var(--navy);
    border: 1px solid var(--green);
    border-radius: var(--border-radius);
    box-shadow: 0 20px 40px -15px var(--navy-shadow);
    z-index: 100;
    min-width: 160px;
    margin-top: 8px;
    overflow: hidden;

    .download-option {
      padding: 12px 16px;
      cursor: pointer;
      font-size: var(--fz-sm);
      font-family: var(--font-mono);
      color: var(--lightest-slate);
      border: none;
      background: transparent;
      width: 100%;
      text-align: left;
      display: flex;
      align-items: center;
      gap: 10px;
      transition: all 0.3s ease;

      &:hover {
        background-color: rgba(100, 255, 218, 0.1);
        color: var(--green);
        transform: translateX(5px);
      }

      svg {
        width: 14px;
        height: 14px;
        opacity: 0.7;
      }
    }
  }
`;

const StyledResumeContent = styled.div`
  background: linear-gradient(135deg, var(--light-navy) 0%, rgba(23, 42, 69, 0.95) 100%);
  border-radius: 24px;
  padding: 40px 80px 80px 80px;
  box-shadow: 0 25px 50px -15px var(--navy-shadow), 0 0 0 1px rgba(100, 255, 218, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(15px);
  position: relative;
  overflow: visible;
  min-height: auto;
  height: auto;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, var(--green), #00d4ff, var(--green));
    background-size: 200% 100%;
    animation: shimmer 3s ease-in-out infinite;
    border-radius: 24px 24px 0 0;
  }

  @keyframes shimmer {
    0%,
    100% {
      background-position: 200% 0;
    }
    50% {
      background-position: -200% 0;
    }
  }

  @media (max-width: 768px) {
    padding: 30px 40px 60px 40px;
    border-radius: 20px;
    min-height: auto;
    height: auto;
  }

  @media (max-width: 480px) {
    padding: 25px 30px 50px 30px;
    border-radius: 16px;
    min-height: auto;
    height: auto;
  }
`;

// Download Button Component
const DownloadButton = () => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const resumeContentRef = useRef(null);

  const handleDownloadPDF = async () => {
    if (!resumeContentRef.current) {return;}

    try {
      const canvas = await html2canvas(resumeContentRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#0a192f',
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save('resume.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
    setIsOpen(false);
  };

  const handleDownloadPNG = async () => {
    if (!resumeContentRef.current) {return;}

    try {
      const canvas = await html2canvas(resumeContentRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#0a192f',
      });

      const link = document.createElement('a');
      link.download = 'resume.png';
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      console.error('Error generating PNG:', error);
    }
    setIsOpen(false);
  };

  const handleDownloadWord = () => {
    // For Word download, we'll create a simple HTML version
    const content = resumeContentRef.current?.innerText || '';
    const blob = new Blob([content], { type: 'application/msword' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'resume.doc';
    link.click();
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (isOpen && !event.target.closest('.download-button-container')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  return {
    component: (
      <StyledDownloadButton className="download-button-container">
        <button className="download-button" onClick={() => setIsOpen(!isOpen)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7,10 12,15 17,10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {t('downloadResume')}
        </button>

        {isOpen && (
          <div className="download-dropdown">
            <button className="download-option" onClick={handleDownloadPDF}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14,2 14,8 20,8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10,9 9,9 8,9" />
              </svg>
              {t('downloadPDF')}
            </button>
            <button className="download-option" onClick={handleDownloadWord}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14,2 14,8 20,8" />
                <line x1="9" y1="13" x2="15" y2="13" />
                <line x1="9" y1="17" x2="15" y2="17" />
              </svg>
              {t('downloadWord')}
            </button>
            <button className="download-option" onClick={handleDownloadPNG}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21,15 16,10 5,21" />
              </svg>
              {t('downloadPNG')}
            </button>
          </div>
        )}
      </StyledDownloadButton>
    ),
    resumeContentRef,
  };
};

const ResumePage = ({ location }) => {
  const revealContainer = useRef(null);
  const { currentLanguage, t } = useLanguage();
  const [resumeContent, setResumeContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { component: downloadButton, resumeContentRef } = DownloadButton();

  useEffect(() => {
    const fetchResumeContent = async () => {
      try {
        let fileName = '/resume.md'; // default Chinese version
        if (currentLanguage === 'en') {
          fileName = '/resume-en.md';
        } else if (currentLanguage === 'zh-TW') {
          fileName = '/resume-zh-TW.md';
        }

        const response = await fetch(fileName);
        if (!response.ok) {
          throw new Error('Failed to fetch resume content');
        }
        const content = await response.text();
        setResumeContent(content);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResumeContent();
  }, [currentLanguage]);

  // 禁用ScrollReveal动画，确保内容立即显示
  // useEffect(() => {
  //   if (prefersReducedMotion || loading) {
  //     return;
  //   }
  //   sr.reveal(revealContainer.current, srConfig());
  // }, [loading, prefersReducedMotion]);

  if (loading) {
    return (
      <Layout location={location}>
        <Helmet title={t('resume')} />
        <main>
          <StyledResumeContainer>
            <StyledResumeHeader>
              <div className="header-left">
                <h1>{t('resume')}</h1>
              </div>
              <div className="header-right">
                <LanguageButton />
              </div>
            </StyledResumeHeader>
            <StyledResumeContent>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '400px',
                  flexDirection: 'column',
                  gap: '20px',
                }}>
                <div
                  style={{
                    width: '60px',
                    height: '60px',
                    border: '3px solid var(--green)',
                    borderTop: '3px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}></div>
                <p style={{ color: 'var(--slate)', fontSize: 'var(--fz-lg)' }}>
                  {t('loadingResume')}
                </p>
              </div>
            </StyledResumeContent>
          </StyledResumeContainer>
        </main>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout location={location}>
        <Helmet title={t('resume')} />
        <main>
          <StyledResumeContainer>
            <StyledResumeHeader>
              <div className="header-left">
                <h1>{t('resume')}</h1>
              </div>
              <div className="header-right">
                <LanguageButton />
              </div>
            </StyledResumeHeader>
            <StyledResumeContent>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '400px',
                  flexDirection: 'column',
                  gap: '20px',
                }}>
                <h2 style={{ color: 'var(--red)', margin: 0 }}>{t('errorLoadingResume')}</h2>
                <p style={{ color: 'var(--slate)', fontSize: 'var(--fz-lg)', textAlign: 'center' }}>
                  {t('errorLoadingResume')}: {error}
                </p>
              </div>
            </StyledResumeContent>
          </StyledResumeContainer>
        </main>
      </Layout>
    );
  }

  return (
    <Layout location={location}>
      <Helmet title={t('resume')} />

      <main>
        <StyledResumeContainer ref={revealContainer}>
          <StyledResumeHeader>
            <div className="header-left">
              <h1>{t('resume')}</h1>
            </div>
            <div className="header-right">
              <LanguageButton />
              {downloadButton}
            </div>
          </StyledResumeHeader>
          <StyledResumeContent
            ref={el => {
              resumeContentRef.current = el;
            }}>
            <div className="markdown-content">
              <ReactMarkdown>{resumeContent}</ReactMarkdown>
            </div>
          </StyledResumeContent>
        </StyledResumeContainer>
      </main>
    </Layout>
  );
};

ResumePage.propTypes = {
  location: PropTypes.object.isRequired,
};

export default ResumePage;
